
import React, { useState, useEffect } from "react";
import { FaMapMarkerAlt, FaStar, FaUtensils, FaSearch, FaTimes, FaShoppingBag, FaTruck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./RestaurantCards.css";

const BASE_URL = "https://backend.maamaas.com/food";

// ─── Inline SVG fallbacks ───────────────────────────────────────────────────
const FALLBACK_BANNER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23f5f5f5'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='15' fill='%23bbb' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E";

const FALLBACK_LOGO =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Ccircle cx='25' cy='25' r='25' fill='%23ff4757'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='20' fill='white' font-family='sans-serif'%3ER%3C/text%3E%3C/svg%3E";

const RestaurantCards = ({ setShowLogin, setIsSignup, isLoggedIn }) => {
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState("All");
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [selectedOrderType, setSelectedOrderType] = useState(null);
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [isPureVeg, setIsPureVeg] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { 
    window.scrollTo({ top: 0, behavior: "smooth" }); 
  }, []);

  // ── Get User Location ──────────────────────────────────────────────────────
  const getUserLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported by your browser"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => {
          console.error("Geolocation error:", err);
          if (err.code === 1) {
            reject(new Error("Location permission denied. Please enable location access."));
          } else if (err.code === 2) {
            reject(new Error("Location unavailable. Please try again."));
          } else {
            reject(new Error("Unable to get your location. Please check your settings."));
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  // ── Normalise vendor data from API response ────────────────────────────────
  const normaliseVendor = (vendor, userLoc = null) => {
    let bannerUrl = FALLBACK_BANNER;
    if (vendor.companyBanner && typeof vendor.companyBanner === 'string' && vendor.companyBanner.trim()) {
      bannerUrl = vendor.companyBanner;
    } else if (vendor.bannerUrl && vendor.bannerUrl.trim()) {
      bannerUrl = vendor.bannerUrl;
    }
    
    let logoUrl = FALLBACK_LOGO;
    if (vendor.companyLogo && typeof vendor.companyLogo === 'string' && vendor.companyLogo.trim()) {
      logoUrl = vendor.companyLogo;
    } else if (vendor.logoUrl && vendor.logoUrl.trim()) {
      logoUrl = vendor.logoUrl;
    }

    let distanceLabel = "N/A";
    let distanceValue = null;
    if (vendor.distance != null && !isNaN(vendor.distance)) {
      distanceValue = parseFloat(vendor.distance);
      distanceLabel = `${distanceValue.toFixed(1)} km`;
    }

    let ratings = "New";
    let ratingValue = null;
    if (vendor.ratings && typeof vendor.ratings === 'number' && vendor.ratings > 0) {
      ratingValue = vendor.ratings;
      ratings = ratingValue.toFixed(1);
    } else if (vendor.avgRating && typeof vendor.avgRating === 'number' && vendor.avgRating > 0) {
      ratingValue = vendor.avgRating;
      ratings = ratingValue.toFixed(1);
    }

    const vendorType = vendor.type || vendor.vendorType || "RESTAURANT";
    const displayType = vendorType.charAt(0).toUpperCase() + vendorType.slice(1).toLowerCase();
    const address = vendor.fullAddress || vendor.addressLine || "Location not available";
    const city = vendor.city || "Hyderabad";

    return {
      vendorId: vendor.vendorId || vendor.id || vendor._id,
      bannerUrl,
      logoUrl,
      companyName: vendor.companyName || vendor.registeredName || vendor.name || "Restaurant",
      Type: displayType,
      addressLine: address,
      city: city,
      ratings: ratings,
      ratingValue: ratingValue,
      distance: distanceLabel,
      distanceValue: distanceValue,
      latitude: vendor.latitude,
      longitude: vendor.longitude,
      orderTypes: vendor.orderTypes || [],
      restaurantStatus: vendor.restaurantStatus !== false,
      startTime: vendor.startTime,
      lastTime: vendor.lastTime,
      establishedYear: vendor.establishedYear,
      isOpen: vendor.restaurantStatus === true,
      isPureVeg: vendor.isPureVeg || vendor.pureVeg || false
    };
  };

  // ── Extract array from API response ────────────────────────────────────────
  const extractVendors = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.vendors)) return data.vendors;
    if (Array.isArray(data?.nearbyVendors)) return data.nearbyVendors;
    if (Array.isArray(data?.results)) return data.results;
    return [];
  };

  // ─── MAIN FUNCTION: Fetch Nearby Restaurants ───────────────────────────────
  const fetchNearbyRestaurants = async () => {
    try {
      setLoading(true);
      setError("");
      setLocationPermissionDenied(false);

      let userLoc;
      try {
        userLoc = await getUserLocation();
        setUserLocation(userLoc);
        console.log("User location obtained:", userLoc);
      } catch (locErr) {
        console.error("Location error:", locErr);
        setLocationPermissionDenied(true);
        setError(locErr.message || "Unable to get your location. Please enable location access.");
        setLoading(false);
        return;
      }

      const { lat, lng } = userLoc;
      const API_URL = `${BASE_URL}/api/user/nearby-vendors/get?latitude=${lat}&longitude=${lng}`;
      console.log("Nearby API URL:", API_URL);

      const response = await axios.get(API_URL, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Nearby vendors API response:", response.data);

      let vendors = extractVendors(response.data);
      
      if (Array.isArray(response.data)) {
        vendors = response.data;
      }
      
      console.log("Extracted vendors:", vendors);

      if (!vendors || vendors.length === 0) {
        setError("No restaurants found near your location. Try expanding your search area.");
        setCards([]);
        setFilteredCards([]);
        return;
      }

      const formatted = vendors
        .map((v) => normaliseVendor(v, userLoc))
        .filter(v => v.restaurantStatus === true)
        .sort((a, b) => {
          const distA = a.distanceValue !== null ? a.distanceValue : Infinity;
          const distB = b.distanceValue !== null ? b.distanceValue : Infinity;
          return distA - distB;
        });

      console.log("Formatted nearby vendors:", formatted);
      setCards(formatted);
      setFilteredCards(formatted);
      
    } catch (err) {
      console.error("Nearby fetch error:", err);
      console.error("Error response:", err.response?.data);
      
      let errorMessage = "Unable to load nearby restaurants. ";
      if (err.response?.status === 404) {
        errorMessage += "Nearby vendors endpoint not found.";
      } else if (err.response?.status === 500) {
        errorMessage += "Server error. Please try again later.";
      } else if (err.code === "ERR_NETWORK") {
        errorMessage += "Network error. Please check your connection.";
      } else if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else {
        errorMessage += "Please try again.";
      }
      
      setError(errorMessage);
      setCards([]);
      setFilteredCards([]);
    } finally {
      setLoading(false);
    }
  };

  // Load nearby restaurants on component mount
  useEffect(() => {
    fetchNearbyRestaurants();
  }, []);

  // Filter restaurants based on search, type, order type, and pure veg
  useEffect(() => {
    let result = [...cards];
    
    // Apply search filter
    if (search) {
      const searchTerm = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.companyName?.toLowerCase().includes(searchTerm) ||
          c.city?.toLowerCase().includes(searchTerm) ||
          c.Type?.toLowerCase().includes(searchTerm) ||
          c.addressLine?.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply type filter
    if (selectedType !== "All") {
      result = result.filter((c) => c.Type === selectedType);
    }
    
    // Apply order type filter
    if (selectedOrderType) {
      result = result.filter((c) => {
        return c.orderTypes && Array.isArray(c.orderTypes) && 
               c.orderTypes.includes(selectedOrderType);
      });
    }
    
    // Apply pure veg filter
    if (isPureVeg) {
      result = result.filter((c) => c.isPureVeg === true);
    }
    
    setFilteredCards(result);
  }, [search, selectedType, cards, selectedOrderType, isPureVeg]);

  // ── Handle order type selection ────────────────────────────────────────────
  const handleOrderTypeSelect = (orderType) => {
    setSelectedOrderType(orderType);
    localStorage.setItem("selectedOrderType", orderType);
  };

  // ── Set order type API call ────────────────────────────────────────────────
  const setOrderTypeToApi = async (vendorId, orderType) => {
    try {
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");
      
      if (!token || !userId) return false;

      const url = `${BASE_URL}/api/cart/create/cart/orderType?userId=${userId}&orderType=${orderType}`;
      console.log("Setting order type URL:", url);
      
      const res = await axios.post(url, {}, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          "Content-Type": "application/json" 
        },
      });
      
      console.log("Order type response:", res.data);
      
      if (res.data?.success || res.status === 200) {
        localStorage.setItem("orderType", orderType);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Order type error:", err.response?.data || err.message);
      localStorage.setItem("orderType", orderType);
      return true;
    }
  };

  // ── Handle card click ──────────────────────────────────────────────────────
  const handleCardClick = async (vendorId) => {
    if (!selectedOrderType) {
      setShowOrderPopup(true);
      setTimeout(() => {
        setShowOrderPopup(false);
      }, 2500);
      return;
    }
    
    if (!vendorId) return;
    
    const loggedIn = isLoggedIn || localStorage.getItem("isLoggedIn") === "true";
    if (!loggedIn) { 
      setIsSignup(false); 
      setShowLogin(true); 
      return; 
    }
    
    await setOrderTypeToApi(vendorId, selectedOrderType);
    
    localStorage.setItem("vendorId", vendorId);
    localStorage.setItem("orderType", selectedOrderType);
    
    navigate(`/Newmenuiteam/${vendorId}`, { 
      state: { 
        orderType: selectedOrderType, 
        vendorId: vendorId 
      } 
    });
  };

  // Get unique restaurant types for filter dropdown
  const getUniqueTypes = () => {
    const types = new Set(cards.map(card => card.Type));
    return ["All", ...Array.from(types).sort()];
  };

  // Handle retry with location
  const handleRetry = () => {
    if (locationPermissionDenied) {
      alert("Please enable location access in your browser settings, then click OK to try again.");
    }
    fetchNearbyRestaurants();
  };

  return (
    <div className="rc-wrapper">
      {showOrderPopup && (
        <div className="rc-order-popup">
          <span className="rc-popup-icon">⚠️</span>
          Please select order type first (Dine In, Take Away, or Delivery)
        </div>
      )}
      
      <p className="rc-heading">
        Nearby <span className="rc-highlight">Restaurants</span>
      </p>

      {/* ── Order Type Selection Buttons ── */}
      <div className="rc-order-type-section">
        <div className="rc-order-buttons">
          <button
            className={`rc-order-btn ${selectedOrderType === "DINE_IN" ? "active" : ""}`}
            onClick={() => handleOrderTypeSelect("DINE_IN")}
          >
            <FaUtensils className="rc-order-icon" />
            Dine In
          </button>

          <button
            className={`rc-order-btn ${selectedOrderType === "TAKEAWAY" ? "active" : ""}`}
            onClick={() => handleOrderTypeSelect("TAKEAWAY")}
          >
            <FaShoppingBag className="rc-order-icon" />
            Take Away
          </button>

          <button
            className={`rc-order-btn ${selectedOrderType === "DELIVERY" ? "active" : ""}`}
            onClick={() => handleOrderTypeSelect("DELIVERY")}
          >
            <FaTruck className="rc-order-icon" />
            Delivery
          </button>

          {/* <button
            className={`pure-veg-toggle ${isPureVeg ? "active" : ""}`}
            onClick={() => setIsPureVeg(!isPureVeg)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: window.innerWidth < 768 ? "4px" : "8px",
              padding: window.innerWidth < 768 ? "6px 10px" : "8px 16px",
              borderRadius: "24px",
              border: "2px solid",
              borderColor: isPureVeg ? "#4CAF50" : "#ddd",
              background: isPureVeg ? "#e8f5e9" : "white",
              color: isPureVeg ? "#2e7d32" : "#666",
              cursor: "pointer",
              fontWeight: "500",
              transition: "all 0.3s ease",
              fontSize: window.innerWidth < 768 ? "11px" : "14px",
              whiteSpace: "nowrap"
            }}
          >
            <span style={{ fontSize: window.innerWidth < 768 ? "14px" : "18px" }}>🌱</span>
            {window.innerWidth < 768 ? (
              isPureVeg ? "Pure Veg" : "Veg Mode"
            ) : (
              isPureVeg ? "Pure Veg Mode ON" : "Pure Veg Mode OFF"
            )}
            {isPureVeg && window.innerWidth >= 768 && (
              <span style={{
                marginLeft: "4px",
                background: "#4CAF50",
                color: "white",
                borderRadius: "12px",
                padding: "2px 8px",
                fontSize: "12px"
              }}>
                Active
              </span>
            )}
            {isPureVeg && window.innerWidth < 768 && (
              <span style={{
                marginLeft: "4px",
                background: "#4CAF50",
                color: "white",
                borderRadius: "10px",
                padding: "2px 6px",
                fontSize: "9px"
              }}>
                ON
              </span>
            )}
          </button> */}
        </div>
      </div>

      {/* Pure Veg Filter Message */}
      {isPureVeg && filteredCards.length > 0 && (
        <div style={{
          marginBottom: "16px",
          padding: "8px 12px",
          background: "#e8f5e9",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#2e7d32",
          textAlign: "center"
        }}>
          🌱 Showing {filteredCards.length} pure vegetarian restaurant(s)
        </div>
      )}

      {/* ── Search and Filter Section ── */}
      {/* <div className="rc-filters">
        <div className="rc-search-wrap">
          <FaSearch className="rc-search-icon" />
          <input
            type="text"
            placeholder="Search restaurants..."
            className="rc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <FaTimes className="rc-clear-icon" onClick={() => setSearch("")} />
          )}
        </div>
        <select
          className="rc-select"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          {getUniqueTypes().map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        {(search || selectedType !== "All" || selectedOrderType || isPureVeg) && (
          <button
            className="rc-clear-btn"
            onClick={() => {
              setSearch("");
              setSelectedType("All");
              setSelectedOrderType(null);
              setIsPureVeg(false);
              localStorage.removeItem("selectedOrderType");
            }}
          >
            Clear All Filters
          </button>
        )}
      </div> */}

      {/* ── Restaurant Grid ── */}
      <div className="rc-grid-wrap">
        {loading ? (
          <div className="rc-state">
            <div className="rc-spinner" />
            <p>Finding restaurants near you...</p>
            <p className="rc-state-small">Getting your location and discovering nearby places</p>
          </div>
        ) : error ? (
          <div className="rc-state">
            <p className="rc-error">{error}</p>
            <button className="rc-retry-btn" onClick={handleRetry}>
              <FaMapMarkerAlt style={{ marginRight: 8 }} />
              Try Again with Location
            </button>
            {locationPermissionDenied && (
              <p className="rc-help-text">
                Tip: Click the location icon in your browser address bar to enable access
              </p>
            )}
          </div>
        ) : filteredCards.length === 0 ? (
          <div className="rc-state">
            <p>No restaurants found</p>
            {selectedOrderType && cards.length > 0 && !isPureVeg && (
              <p style={{ fontSize: "13px", marginTop: "8px", color: "#666" }}>
                {cards.length} restaurant(s) nearby, but none support {selectedOrderType.replace("_", " ")}
              </p>
            )}
            {isPureVeg && cards.length > 0 && (
              <p style={{ fontSize: "13px", marginTop: "8px", color: "#666" }}>
                No pure vegetarian restaurants found nearby
              </p>
            )}
            {(search || selectedType !== "All" || selectedOrderType || isPureVeg) && (
              <button
                className="rc-retry-btn"
                onClick={() => { 
                  setSearch(""); 
                  setSelectedType("All"); 
                  setSelectedOrderType(null);
                  setIsPureVeg(false);
                  localStorage.removeItem("selectedOrderType");
                }}
                style={{ marginTop: "12px", background: "#666" }}
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <div className="rc-grid">
            {filteredCards.map((card) => (
              <div
                key={card.vendorId}
                className="rc-card"
                onClick={() => handleCardClick(card.vendorId)}
              >
                {/* Banner Section */}
                <div className="rc-banner-wrap">
                  <img
                    src={card.bannerUrl}
                    alt={card.companyName}
                    className="rc-banner"
                    onError={(e) => {
                      if (e.target.src !== FALLBACK_BANNER)
                        e.target.src = FALLBACK_BANNER;
                    }}
                  />
                  <img
                    src={card.logoUrl}
                    alt="logo"
                    className="rc-logo"
                    onError={(e) => {
                      if (e.target.src !== FALLBACK_LOGO)
                        e.target.src = FALLBACK_LOGO;
                    }}
                  />
                  {!card.restaurantStatus && (
                    <div className="rc-closed-badge">Closed</div>
                  )}
                  {card.isPureVeg && (
                    <div className="pure-veg-badge">
                      🌱 PURE VEG
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="rc-body">
                  <div className="rc-row">
                    <h4 className="rc-name" title={card.companyName}>
                      {card.companyName}
                      {card.isPureVeg && !isPureVeg && (
                        <span style={{ marginLeft: "6px", fontSize: "12px" }}>🌱</span>
                      )}
                    </h4>
                    <div className="rc-rating">
                      <FaStar className="rc-star" />&nbsp;{card.ratings}
                    </div>
                  </div>

                  <p className="rc-type">
                    <FaUtensils className="rc-icon-red" />&nbsp;{card.Type}
                  </p>

                  <div className="rc-location-row">
                    <span className="rc-address" title={card.addressLine}>
                      <FaMapMarkerAlt className="rc-icon-red" />&nbsp;
                      {card.addressLine?.length > 30
                        ? card.addressLine.substring(0, 30) + "…"
                        : card.addressLine}
                    </span>
                    <span className="rc-distance">
                      📍 {card.distance}
                    </span>
                  </div>
                  
                  {/* Order types available */}
                  {card.orderTypes && card.orderTypes.length > 0 && (
                    <div className="rc-order-types">
                      {/* {card.orderTypes.map((type, idx) => (
                        <span 
                          key={idx} 
                          className={`rc-order-badge ${selectedOrderType === type ? 'active-order-type' : ''}`}
                        >
                          {type === "DINE_IN" ? "🍽️ Dine In" : 
                           type === "TAKEAWAY" ? "🛍️ Take Away" : 
                           type === "DELIVERY" ? "🚚 Delivery" : 
                           type === "CATERING" ? "🎉 Catering" : type}
                        </span>
                      ))} */}
                    </div>
                  )}
                  
                  {/* Timing info if available */}
                  {card.startTime && card.lastTime && (
                    <div className="rc-timing">
                      ⏰ {card.startTime.slice(0,5)} - {card.lastTime.slice(0,5)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .rc-wrapper {
          min-height: 75vh;
          background: transparent;
          padding: 20px 16px 40px;
          font-family: 'Segoe UI', sans-serif;
        }
        .rc-heading {
          text-align: center;
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 8px;
          color: #222;
        }
        .rc-highlight { color: #ee6521; }

        .rc-order-type-section {
          border-radius: 16px;
          padding: 16px 20px;
          margin: 20px auto;
          max-width: 800px;
          text-align: center;
        }
        .rc-order-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .rc-order-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: #ee6521;
          border: none;
          border-radius: 50px;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .rc-order-btn:hover {
          background: #d55a1a;
          transform: translateY(-2px);
        }
        .rc-order-btn.active {
          background: #28a745;
          color: white;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .rc-order-icon {
          font-size: 16px;
        }

        .rc-filters {
          display: flex;
          gap: 10px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
          max-width: 860px;
          margin-left: auto;
          margin-right: auto;
        }
        .rc-search-wrap {
          position: relative;
          flex: 1;
          min-width: 250px;
          max-width: 450px;
        }
        .rc-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #aaa;
          font-size: 13px;
          pointer-events: none;
        }
        .rc-clear-icon {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #aaa;
          font-size: 13px;
          cursor: pointer;
        }
        .rc-search {
          width: 100%;
          padding: 12px 36px;
          border: 1.5px solid #e0e0e0;
          border-radius: 50px;
          font-size: 14px;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }
        .rc-search:focus { border-color: #ee6521; }
        .rc-select {
          padding: 12px 18px;
          border: 1.5px solid #e0e0e0;
          border-radius: 50px;
          font-size: 14px;
          background: #fff;
          cursor: pointer;
          outline: none;
        }
        .rc-clear-btn {
          padding: 12px 20px;
          background: #fff;
          border: 1.5px solid #ddd;
          border-radius: 50px;
          cursor: pointer;
          font-size: 14px;
          color: #666;
          transition: all 0.2s;
        }
        .rc-clear-btn:hover {
          background: #f5f5f5;
          border-color: #ee6521;
        }

        .rc-grid-wrap { max-width: 1400px; margin: 0 auto; }
        .rc-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 22px;
        }
        @media (max-width: 600px) {
          .rc-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
          .rc-heading { font-size: 24px; }
          .rc-filters { gap: 8px; }
          .rc-search-wrap { min-width: 180px; }
          .rc-order-btn {
            padding: 8px 16px;
            font-size: 12px;
          }
        }

        .rc-card {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 3px 14px rgba(0,0,0,0.09);
          cursor: pointer;
          transition: transform 0.25s, box-shadow 0.25s;
        }
        .rc-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 28px rgba(0,0,0,0.15);
        }

        .rc-banner-wrap {
          position: relative;
          height: 165px;
          background: #f0f0f0;
          overflow: hidden;
        }
        .rc-banner {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .rc-logo {
          position: absolute;
          top: 10px;
          left: 10px;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: 2.5px solid #fff;
          object-fit: cover;
          background: #fff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        }
        .rc-closed-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #6c757d;
          color: #fff;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 700;
        }
        .pure-veg-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #4CAF50;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: bold;
          z-index: 1;
        }

        .rc-body { padding: 12px 12px 14px; }
        .rc-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .rc-name {
          font-size: 15px;
          font-weight: 700;
          color: #222;
          margin: 0;
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .rc-rating {
          display: flex;
          align-items: center;
          background: #f8f8f8;
          border-radius: 10px;
          padding: 3px 7px;
          font-size: 12px;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          margin-left: 6px;
        }
        .rc-star { color: #ffc107; font-size: 11px; margin-right: 3px; }
        .rc-type {
          display: flex;
          align-items: center;
          font-size: 12px;
          color: #666;
          margin: 0 0 8px;
        }
        .rc-icon-red { color: #ee6521; margin-right: 4px; font-size: 11px; }
        .rc-location-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          color: #999;
          gap: 8px;
        }
        .rc-address {
          display: flex;
          align-items: center;
          flex: 1;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
        .rc-distance {
          background: #f0f8ff;
          border-radius: 10px;
          padding: 2px 8px;
          font-size: 10px;
          font-weight: 600;
          color: #0066cc;
          white-space: nowrap;
          display: flex;
          align-items: center;
        }
        .rc-order-types {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          margin-top: 10px;
        }
        .rc-order-badge {
          background: #f0f0f0;
          color: #666;
          font-size: 9px;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
        }
        .rc-order-badge.active-order-type {
          background: #28a745;
          color: white;
        }
        .rc-timing {
          font-size: 10px;
          color: #888;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .rc-order-popup {
          position: fixed;
          top: 20%;
          left: 50%;
          transform: translateX(-50%);
          background: #fff3cd;
          color: #856404;
          padding: 12px 20px;
          border-radius: 10px;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 6px 18px rgba(0,0,0,0.15);
          animation: popupFade 0.3s ease;
          z-index: 1000;
          white-space: nowrap;
        }
        .rc-popup-icon { font-size: 18px; }

        @keyframes popupFade {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }

        @media (max-width: 600px) {
          .rc-banner-wrap { height: 120px; }
          .rc-logo { width: 34px; height: 34px; top: 7px; left: 7px; }
          .rc-body { padding: 9px 10px 10px; }
          .rc-name { font-size: 12px; }
          .rc-rating { font-size: 10px; padding: 2px 5px; }
          .rc-type { font-size: 10px; }
          .rc-location-row { font-size: 9px; }
          .rc-order-popup { font-size: 12px; padding: 8px 16px; white-space: nowrap; }
          .pure-veg-badge { font-size: 9px; padding: 2px 6px; top: 6px; right: 6px; }
        }

        .rc-state {
          text-align: center;
          padding: 60px 20px;
          color: #888;
        }
        .rc-error { 
          color: #ee6521; 
          margin-bottom: 16px; 
          font-weight: 500;
          font-size: 15px;
        }
        .rc-state-small {
          font-size: 12px;
          color: #aaa;
          margin-top: 8px;
        }
        .rc-help-text {
          font-size: 12px;
          color: #888;
          margin-top: 16px;
        }
        .rc-retry-btn {
          display: inline-flex;
          align-items: center;
          padding: 10px 24px;
          background: #ee6521;
          color: #fff;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          transition: background 0.2s;
        }
        .rc-retry-btn:hover {
          background: #d55a1a;
        }
        .rc-spinner {
          width: 44px;
          height: 44px;
          border: 4px solid #f0f0f0;
          border-top-color: #ee6521;
          border-radius: 50%;
          animation: rc-spin 0.8s linear infinite;
          margin: 0 auto 16px;
        }
        @keyframes rc-spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default RestaurantCards;